import React, { FC, useState } from 'react';
import { Button, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import PublishOutlinedIcon from '@mui/icons-material/Publish';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import { format, parseISO } from 'date-fns';

import COEEllipsisPopover from '../general/coe-ellipsis-popover/coe-ellipsis-popover.component';
import AddSessionChoiceDialog from '../dialogs/add-session-choice.dialog';
import { UserAvatar } from '../user-avatar/user-avatar.component';
import { DAY_ORDER, SplitWeekStats, WeekSchedule, isSameCalendarDay } from '../../helpers/team-training-plan.helpers';
import { formatDuration, getMeasurementUnit } from '../../helpers/general.helpers';
import { Group } from '../../models/training.models';
import { palette } from '../../theme';

import './tp-week-calendar.component.css';

export interface TpWeekCalendarProps {
  accent: 'team' | 'group';
  title: string;
  subtitle?: string;
  weekSchedule: WeekSchedule;
  teamTrainingsByDay?: WeekSchedule;
  groupId?: string;
  group?: Group;
  stats: SplitWeekStats;
  loading?: boolean;
  disabled?: boolean;
  onEdit: (trainingId: string) => void;
  onPublish: (trainingId: string) => void;
  onDuplicate: (trainingId: string) => void;
  onDelete: (trainingId: string) => void;
  onPublishWeek: () => void;
  onWeeklyPlans: () => void;
  onAddNew: (date: Date) => void;
  onAddFromLibrary: (date: Date) => void;
  onOpenMembers?: () => void;
}

const TpWeekCalendar: FC<TpWeekCalendarProps> = (props) => {
  const [choiceDay, setChoiceDay] = useState<{ date: string; label: string } | null>(null);

  const isTeam = props.accent === 'team';
  const accentColor = isTeam ? palette.primary.main : palette.purple.main;
  const accentLight = isTeam ? palette.primary.light : palette.purple.light;

  const getDayTrainings = (dayName: string): { training: any; isTeamShared: boolean }[] => {
    const groupTrainings = props.weekSchedule[dayName]?.trainings || [];
    if (isTeam) {
      return groupTrainings.map((training) => ({ training, isTeamShared: false }));
    }

    const teamTrainings = props.teamTrainingsByDay?.[dayName]?.trainings || [];
    const result: { training: any; isTeamShared: boolean }[] = [];

    teamTrainings.forEach((training) => {
      if (props.groupId && training.excludedGroupIds?.includes(props.groupId)) {
        return;
      }
      result.push({ training, isTeamShared: true });
    });

    groupTrainings.forEach((training) => {
      result.push({ training, isTeamShared: false });
    });

    return result;
  };

  const handleAddDate = (isoDate: string): Date => {
    const givenDate = new Date(isoDate);
    const currentTime = new Date();
    const nearest5Minutes = Math.ceil(currentTime.getMinutes() / 5) * 5;
    givenDate.setHours(currentTime.getHours(), nearest5Minutes, 0, 0);
    return givenDate;
  };

  const renderAvatars = () => {
    if (!props.group?.members?.length) return null;
    const members = props.group.members;
    const visible = members.slice(0, 4);
    const remaining = members.length - visible.length;

    return (
      <div
        className="tp-week-calendar__avatars"
        onClick={props.onOpenMembers}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            props.onOpenMembers?.();
          }
        }}
        aria-label={`View ${props.group.name} members`}
      >
        {visible.map((member: any, index: number) => (
          <UserAvatar
            key={member.id || member.userId || index}
            name={member.name}
            firstname={member.firstname}
            lastname={member.lastname}
            src={member.profileImage}
            userId={member.id || member.userId}
            size={28}
            sx={{ marginLeft: index === 0 ? 0 : '-8px', border: '2px solid #fff', zIndex: 4 - index }}
          />
        ))}
        {remaining > 0 && (
          <div className="tp-week-calendar__avatar-more" style={{ backgroundColor: accentLight, color: accentColor }}>
            +{remaining}
          </div>
        )}
      </div>
    );
  };

  const renderSession = (training: any, isTeamShared: boolean) => (
    <div
      key={`${isTeamShared ? 'team' : 'group'}-${training.id}`}
      className={`tp-session${isTeamShared ? ' tp-session--team' : ''}`}
      onClick={() => props.onEdit(training.id)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter') props.onEdit(training.id);
      }}
    >
      {isTeamShared && <span className="tp-team-tag">Team</span>}
      <div className="tp-sess-name">{training.title}</div>
      <div className="tp-sess-time">
        {format(parseISO(training.time), 'h:mm a')}
        {training.location ? ` • ${training.location}` : ''}
      </div>
      <div className="tp-stats-line">
        {formatDuration(training.duration)} • {training.distance} {getMeasurementUnit()}
      </div>
      <div className="tp-session-footer">
        {training.published ? (
          <span className="tp-badge-live">
            <CheckCircleOutlinedIcon style={{ fontSize: 12 }} />
            Live
          </span>
        ) : (
          <Button
            variant="contained"
            size="small"
            className="tp-badge-publish"
            onClick={(e) => {
              e.stopPropagation();
              props.onPublish(training.id);
            }}
            style={{ backgroundColor: palette.primary.main, color: palette.background.paper }}
          >
            Publish
          </Button>
        )}
        <div onClick={(e) => e.stopPropagation()}>
          <COEEllipsisPopover
            actions={[
              {
                title: 'Edit',
                color: 'info',
                action: () => props.onEdit(training.id),
              },
              {
                title: 'Publish',
                color: 'info',
                action: () => props.onPublish(training.id),
                hide: training.published,
              },
              {
                title: 'Duplicate',
                color: 'info',
                action: () => props.onDuplicate(training.id),
              },
              {
                title: 'Delete',
                color: 'error',
                action: () => props.onDelete(training.id),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className={`tp-week-calendar tp-week-calendar--${props.accent}`}>
      <div className="tp-week-calendar__header">
        <div className="tp-week-calendar__title-row">
          <div className="tp-week-calendar__title-left">
            <div className="tp-week-calendar__icon" style={{ backgroundColor: accentLight }}>
              {isTeam ? (
                <PeopleAltOutlinedIcon style={{ fontSize: 17, color: accentColor }} />
              ) : (
                <GridViewOutlinedIcon style={{ fontSize: 17, color: accentColor }} />
              )}
            </div>
            <Typography
              variant="h6"
              style={{ fontWeight: 700, fontSize: 16, color: isTeam ? palette.text.primary : accentColor }}
            >
              {props.title}
            </Typography>
            {props.subtitle && (
              <div
                style={{
                  backgroundColor: accentLight,
                  color: accentColor,
                  borderRadius: 20,
                  padding: '2px 10px',
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {props.subtitle}
              </div>
            )}
          </div>
          {renderAvatars()}
        </div>

        <div className="tp-stats">
          <div className="tp-stats-row">
            <div className="tp-stats-left">
              <span className="tp-stats-tag tp-stats-tag--draft">Draft</span>
              <div className="tp-stats-metric">
                <span className="tp-stats-metric__label">Duration</span>
                <span className="tp-stats-metric__value tp-stats-metric__value--draft">
                  {props.stats.draft.duration}
                </span>
              </div>
              <div className="tp-stats-metric">
                <span className="tp-stats-metric__label">Distance</span>
                <span className="tp-stats-metric__value tp-stats-metric__value--draft">
                  {props.stats.draft.distance} {getMeasurementUnit()}
                </span>
              </div>
              <div className="tp-stats-metric">
                <span className="tp-stats-metric__label">Sessions</span>
                <span className="tp-stats-metric__value tp-stats-metric__value--draft">
                  {props.stats.draft.sessions}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<CalendarMonthOutlinedIcon style={{ fontSize: 14 }} />}
                onClick={props.onWeeklyPlans}
                style={{
                  backgroundColor: palette.background.paper,
                  color: palette.text.primary,
                  textTransform: 'none',
                  fontSize: 12,
                  borderColor: palette.divider,
                  borderRadius: 6,
                  boxShadow: 'none',
                }}
              >
                Weekly Plans
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<PublishOutlinedIcon style={{ fontSize: 14 }} />}
                onClick={props.onPublishWeek}
                disabled={props.stats.unpublishedCount === 0 || props.disabled}
                style={{
                  backgroundColor: accentColor,
                  color: palette.background.paper,
                  textTransform: 'none',
                  fontSize: 12,
                  borderRadius: 6,
                  boxShadow: 'none',
                }}
              >
                Publish Week
                {props.stats.unpublishedCount > 0 && (
                  <span className="tp-pub-badge">{props.stats.unpublishedCount}</span>
                )}
              </Button>
            </div>
          </div>
          <div className="tp-stats-row">
            <div className="tp-stats-left">
              <span className="tp-stats-tag tp-stats-tag--live">Live</span>
              <div className="tp-stats-metric">
                <span className="tp-stats-metric__label">Duration</span>
                <span className="tp-stats-metric__value tp-stats-metric__value--live">{props.stats.live.duration}</span>
              </div>
              <div className="tp-stats-metric">
                <span className="tp-stats-metric__label">Distance</span>
                <span className="tp-stats-metric__value tp-stats-metric__value--live">
                  {props.stats.live.distance} {getMeasurementUnit()}
                </span>
              </div>
              <div className="tp-stats-metric">
                <span className="tp-stats-metric__label">Sessions</span>
                <span className="tp-stats-metric__value tp-stats-metric__value--live">{props.stats.live.sessions}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="tp-grid-scroll">
        <div className="tp-grid">
          {DAY_ORDER.map((dayName) => {
            const weekDay = props.weekSchedule[dayName];
            if (!weekDay) return null;
            const isToday = isSameCalendarDay(weekDay.date);
            const sessions = getDayTrainings(dayName);

            return (
              <div key={dayName} className={`tp-day${isToday ? ' tp-day--today' : ''}`}>
                <div className={`tp-day-label${isToday ? ' tp-day-label--today' : ''}`}>
                  {format(parseISO(weekDay.date), 'EEE d')}
                  {isToday && <span className="tp-today-badge">Today</span>}
                </div>

                {sessions.length === 0 ? (
                  <div className="tp-day-empty">No sessions</div>
                ) : (
                  sessions.map(({ training, isTeamShared }) => renderSession(training, isTeamShared))
                )}

                <button
                  type="button"
                  className="tp-day-add"
                  disabled={props.disabled}
                  onClick={() =>
                    setChoiceDay({
                      date: weekDay.date,
                      label: format(parseISO(weekDay.date), 'EEEE'),
                    })
                  }
                >
                  <AddIcon style={{ fontSize: 14 }} />
                  Add
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <AddSessionChoiceDialog
        open={Boolean(choiceDay)}
        dayLabel={choiceDay?.label || ''}
        onClose={() => setChoiceDay(null)}
        onNewTraining={() => {
          if (!choiceDay) return;
          props.onAddNew(handleAddDate(choiceDay.date));
          setChoiceDay(null);
        }}
        onTrainingLibrary={() => {
          if (!choiceDay) return;
          props.onAddFromLibrary(new Date(choiceDay.date));
          setChoiceDay(null);
        }}
      />
    </div>
  );
};

export default TpWeekCalendar;
